import { FunctionComponent, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { InsetText } from '@atoms/*';
import { CallToActionCardProps, InformationBox } from '@molecules/';
import { useCycleStore } from '@cycle-hangars/store/store';
import { BeforeYouStartTemplate, ContentBody } from '@templates';
import { RoutesList } from '@cycle-hangars/routes/Routes';
import { v4 as uuidv4 } from 'uuid';
import {
  FORM_LINK_TEXT,
  FORM_LINK_SUBTEXT,
  CHAT_LINK_TEXT,
  CHAT_LINK_SUBTEXT,
} from '@constants';

const ASSISTANT_URL = import.meta.env.VITE_ASSISTANT_URL;

export const BeforeYouStart: FunctionComponent = () => {
  const navigate = useNavigate();
  const { updateSessionId, resetIssueDetails, resetConfirmationInformation } =
    useCycleStore();
  const contentTitle = 'Report a cycle hangar issue';

  useEffect(() => {
    resetIssueDetails();
    resetConfirmationInformation();
  }, []);

  const sessionId = uuidv4();
  const initiateForm = () => {
    updateSessionId(sessionId);
    navigate(RoutesList.UPLOAD_PHOTO);
  };

  const reportItCTA: CallToActionCardProps = {
    linkText: FORM_LINK_TEXT,
    linkSubtext: FORM_LINK_SUBTEXT,
    onClick: initiateForm,
  };

  const reportItAssistantRoute = `${ASSISTANT_URL}?category=cycle_hangars`;
  const reportItAssistantCTA: CallToActionCardProps = {
    linkText: CHAT_LINK_TEXT,
    linkSubtext: CHAT_LINK_SUBTEXT,
    externalHref: reportItAssistantRoute,
  };

  const callToActionCards = [reportItAssistantCTA, reportItCTA];

  const detailsWeNeedContent = {
    sectionTitle: 'To help us resolve the issue, we need to know:',
    itemList: [
      'the cycle hangar ID',
      'a description of the issue',
      'the location of the cycle hangar',
    ],
  };

  const usefulToIncludeContent = {
    sectionHeader: `It's useful to provide:`,
    itemList: ['photos of the issue', 'your contact details'],
  };

  const bodyContent = [detailsWeNeedContent, usefulToIncludeContent];

  const linkObjectArrays = [
    {
      level: 1,
      title: 'Home',
      href: 'https://www.westminster.gov.uk/',
    },
    {
      level: 2,
      title: 'Report it',
      href: 'https://www.westminster.gov.uk/report-it',
    },
    {
      level: 3,
      title: `Report a cycle hangar issue`,
    },
  ];

  return (
    <BeforeYouStartTemplate
      contentTitle={contentTitle}
      contentSubtext={
        <>
          <ContentBody content={bodyContent} />
          <InsetText
            content={
              <>
                If your issue is about a graffiti on a cycle hangar,{' '}
                <a
                  className="text-action-link underline"
                  href="https://mywestminster.westminster.gov.uk/report-it/waste?type=graffiti_posters_and_stickers"
                  rel="noopener noreferrer"
                >
                  report it here
                </a>
                .
              </>
            }
          />
          <InformationBox
            infoText="If your report is about a theft, you should also report it to the police."
            variant="warning"
            data-testid="information-box"
          />
        </>
      }
      callToActionCards={callToActionCards}
      linkObjectArrays={linkObjectArrays}
    />
  );
};
